import React, { useEffect, useState, useContext } from "react";
import axiosInstance from "utils/axiosInstance";
import { AuthContext } from "context/AuthContext"; // Import AuthContext
import Card from "components/card";
import { toast } from "react-toastify";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import Modal from 'components/Modal'
import { FaPersonCircleXmark } from "react-icons/fa6";
import {
  Badge,
  Button,
  EmptyState,
  Pagination,
  SortIndicator,
  tdClass,
  tdStrongClass,
  thClass,
  thLabelClass,
  theadRowClass,
  trClass,
} from "components/ui";

type RowObj = {
  id: number;
  name: [string, boolean];
  createdAt: string;
  clasificacion: string; // e.g. "Inactivo"
  quantity: number;      // total # of Fútbol events attended (all time)
  penas: number;         // total # of Peñas events attended (all time)
  date: string;
};

function JugadoresInactivos() {
  // Access auth state from context
  const { isLoggedIn } = useContext(AuthContext);
  // TABLE / SORTING / DATA
  const [sorting, setSorting] = useState<SortingState>([]);
  const [data, setData] = useState<RowObj[]>([]);

  // REACTIVAR
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [restorePersonaId, setRestorePersonaId] = useState<number | null>(null);

  // PAGINATION
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Fetch inactive + all-time stats
  const fetchInactivePlayers = async () => {
    try {
      // 1) Inactive players
      const { data: inactiveRes } = await axiosInstance.get("/personas/inactive");

      // 2) All-time Fútbol
      const { data: futbolRes } = await axiosInstance.get(
        "/eventos/futbol-resumen?year=all"
      );
      // 3) All-time Peñas
      const { data: penasRes } = await axiosInstance.get(
        "/eventos/penas-resumen?year=all"
      );

      // Build maps
      const futbolMap: Record<number, number> = {};
      futbolRes.forEach((item: any) => {
        futbolMap[item.id] = item.futbol;
      });

      const penasMap: Record<number, number> = {};
      penasRes.forEach((item: any) => {
        penasMap[item.id] = item.penas;
      });

      // Transform
      const transformed: RowObj[] = inactiveRes.map((p: any) => {
        const personaId = p.id;
        return {
          id: personaId,
          name: [p.nombre, true],
          createdAt: p.createdAt,
          clasificacion: "Descendido",
          quantity: futbolMap[personaId] || 0, // total Fútbol
          penas: penasMap[personaId] || 0,     // total Peñas
          date: new Date().toLocaleDateString(),
        };
      });
      setData(transformed);

    } catch (error) {
      console.error("Error fetching inactive players or stats:", error);
    }
  };

  // Call fetch on mount + listen for "player-deleted"
  useEffect(() => {
    // 1) Fetch immediately
    fetchInactivePlayers();

    // 2) Listen for "player-deleted" events
    function onPlayerDeleted(e: CustomEvent) {
      // Just re-fetch
      fetchInactivePlayers();
    }
    window.addEventListener("player-deleted", onPlayerDeleted as EventListener);

    // cleanup
    return () => {
      window.removeEventListener("player-deleted", onPlayerDeleted as EventListener);
    };
  }, []);

  // RESTORE
  const handleRestore = async () => {
    if (!restorePersonaId) return;
    try {
      await axiosInstance.put(`/personas/restore/${restorePersonaId}`);
      fetchInactivePlayers();
      //Close the modal
      setIsRestoreModalOpen(false);
    } catch (error) {
      console.error("Error restoring player:", error);
      toast.error("No se pudo reactivar al jugador.");
    }
  };

  // TABLE CONFIG
  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    // NAME
    columnHelper.accessor("name", {
      id: "name",
      header: () => <span className={thLabelClass}>Nombre</span>,
      cell: (info) => (
        <span className="truncate font-semibold">{info.getValue()[0]}</span>
      ),
    }),

    // ESTADO
    columnHelper.accessor("clasificacion", {
      id: "clasificacion",
      header: () => <span className={thLabelClass}>Estado</span>,
      cell: (info) => <Badge tone="neutral">{info.getValue()}</Badge>,
    }),

    // FÚTBOL (PJ)
    columnHelper.accessor("quantity", {
      id: "quantity",
      header: () => <span className={thLabelClass}>PJ</span>,
      cell: (info) => (
        <span className="tabular font-semibold">{info.getValue()}</span>
      ),
    }),

    // PENAS
    columnHelper.accessor("penas", {
      id: "penas",
      header: () => <span className={thLabelClass}>Peñas</span>,
      cell: (info) => (
        <span className="tabular font-semibold">{info.getValue()}</span>
      ),
    }),

    // REACTIVAR
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <div className="flex justify-end">
          {isLoggedIn && (
            <Button
              variant="success-soft"
              size="sm"
              onClick={() => {
                setRestorePersonaId(info.row.original.id);
                setIsRestoreModalOpen(true);
              }}
              disabled={!isLoggedIn}
            >
              Reactivar
            </Button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // RENDER
  return (
    <div>
      <div className="pb-5 text-left">
        <h2 className="text-[17px] font-semibold text-gray-900 dark:text-white">
          Inactivos
        </h2>
        <p className="mt-1 text-[13px] text-gray-700 dark:text-gray-400">
          {data.length} {data.length === 1 ? "jugador dado de baja" : "jugadores dados de baja"}
        </p>
      </div>

      <Card extra="flex w-full flex-col border-none bg-transparent p-0 text-left shadow-none dark:!bg-transparent">
        {data.length === 0 ? (
          <EmptyState
            icon={<FaPersonCircleXmark />}
            title="No hay jugadores inactivos"
            description="Los jugadores que des de baja van a aparecer acá, listos para reactivar."
          />
        ) : (
        <div className="-mx-1 w-full overflow-x-auto px-1">
          <table className="w-full sm:min-w-[420px]">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={theadRowClass}>
                  {headerGroup.headers.map((header) => {
                    const colId = header.column.id;
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className={`${thClass}
                          ${isLoggedIn && colId === "clasificacion"
                            ? "hidden sm:table-cell"
                            : ""}
                          ${!isLoggedIn && colId === "actions"
                            ? "hidden"
                            : ""}
                        `}
                      >
                        <span className="inline-flex items-center gap-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <SortIndicator direction={header.column.getIsSorted()} />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={trClass}>
                  {row.getVisibleCells().map((cell) => {
                    const colId = cell.column.id;
                    return (
                      <td
                        key={cell.id}
                        className={`${colId === "name" ? tdStrongClass : tdClass}
                          ${isLoggedIn && colId === "clasificacion"
                            ? "hidden sm:table-cell"
                            : ""}
                          ${!isLoggedIn && colId === "actions"
                            ? "hidden"
                            : ""}
                        `}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* REACTIVAR CONFIRMATION MODAL */}
        {isLoggedIn && (
          <Modal isOpen={isRestoreModalOpen} onClose={() => setIsRestoreModalOpen(false)}>
            <h3 className="select-none text-[17px] font-semibold text-gray-900 dark:text-white">
              ¿Reactivar al jugador?
            </h3>
            <p className="mt-2 select-none text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              Vuelve al plantel activo y podrá marcarse en las asistencias.
            </p>
            <div className="mt-6 flex select-none justify-end gap-2">
              <Button variant="secondary" onClick={() => setIsRestoreModalOpen(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleRestore}>
                Reactivar
              </Button>
            </div>
          </Modal>
        )}

        {/* PAGINATION */}
        <Pagination
          className="mt-5 border-t border-gray-200/70 pt-5 dark:border-white/10"
          pageIndex={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
        />
      </Card>
    </div>
  );
}

export default JugadoresInactivos;
